import { useContext } from 'react';
import { isAxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showNotification } from '@mantine/notifications';

import { logError } from '~/common/utils/log';
import { UserContext } from '~/components/providers/UserProvider';
import { ContestQueryKeys, ContestMutationKeys } from '~/domains/contest/constants/query';
import { dismissContestRestart } from '~/services/contests/contests.service.api';
import { dismissContestRestartV2 } from '~/services/contests/contests-v2.service.api';

type DismissContestRestartArgs = {
  contestId: string;
  isSplashV2?: boolean;
};

export function useDismissContestRestart() {
  const { t } = useTranslation('contest');
  const queryClient = useQueryClient();
  const { userId } = useContext(UserContext);

  const { mutate, isLoading } = useMutation({
    mutationKey: ContestMutationKeys.DISMISS_CONTEST_RESTART(),
    mutationFn: ({ contestId, isSplashV2 }: DismissContestRestartArgs) =>
      isSplashV2 ? dismissContestRestartV2(contestId) : dismissContestRestart(contestId),
    onSuccess: () => {
      // Invalidate the query cache to properly refresh the restarts list
      // We need to wait 500ms because the backend is sometimes returning stale data if we query it immediately
      const cacheInvalidationTimeout = setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ContestQueryKeys.GET_CONTESTS_RESTARTS({ userId }),
        });
      }, 500);

      return () => clearTimeout(cacheInvalidationTimeout);
    },
    onError: (error: unknown) => {
      if (isAxiosError(error) && error.response?.data?.message) {
        showNotification({
          message: error.response?.data?.message,
          color: 'red',
          autoClose: 5000,
        });
        return;
      }

      showNotification({
        message: t('common.contestNotificationWidget.error'),
        color: 'red',
        autoClose: 5000,
      });
      logError(error);
    },
  });

  return {
    dismissContestRestart: mutate,
    isLoading,
  };
}
